/** In-app navigation & crisis support — shared by mobile Tools and API app-guide */
export const CRISIS_HOTLINES = [
    {
        id: 'lagos-mh',
        name: 'Lagos State Mental Health Hotline',
        description: '24/7 · Free · Confidential',
        phone: '08006870000',
        phoneDisplay: '0800-687-0000',
    },
    {
        id: 'fmoh',
        name: 'FMOH Suicide Prevention Line',
        description: 'National · 24/7',
        phone: '08068786556',
        phoneDisplay: '0806-878-6556',
    },
    {
        id: 'nema',
        name: 'NEMA Emergency',
        description: 'Immediate danger',
        phone: '112',
        phoneDisplay: '112',
    },
    {
        id: 'mani',
        name: 'Mentally Aware Nigeria Initiative',
        description: 'Crisis counseling · Mon–Fri 9am–5pm',
        phone: '08091116264',
        phoneDisplay: '0809-111-6264',
    },
];
export const APP_GUIDE_SUGGESTED_PROMPTS = [
    'How do I book a therapist?',
    'Where is my next session?',
    'How do I track my mood?',
    'What does the assessment do?',
    'How do referrals work?',
    'I feel overwhelmed right now',
];
/** Context injected into AI prompts — keep in sync with mobile tabs */
export const APP_GUIDE_SYSTEM_CONTEXT = `You are MyLighthouse Guide, a warm in-app assistant for the MyLighthouse mental health app (Nigeria).
Help clients navigate the app and use self-help tools. You are NOT a therapist and do not diagnose.
App structure:
- Home tab: daily mood check-in, affirmation, recommended therapists, article previews, referral banner, streak/badges summary.
- Therapy tab: upcoming and past bookings, session join, book new sessions, session progress.
- Tools tab: Quick calm (breathing), grounding exercise, worry dump journal, session prep, wellness activities library, progress tracker (PHQ-9/GAD-7), group sessions, badges, crisis hotlines, and this AI guide.
- Profile tab: name, phone, emergency contact, language (English/Yoruba/Igbo/Hausa), notifications, MFA, sign out.
Stack screens: Find Therapist list, Therapist Profile, Book Session, Assessment (PHQ-9/GAD-7), Journal, Learn articles, Refer & Earn wallet, Notifications.
Crisis: direct users to Crisis Support in Tools or call 112 / Lagos hotline 08006870000 if in immediate danger.
Keep answers under 120 words, plain Nigerian English, actionable steps numbered when helpful.`;
const NAV_PATTERNS = [
    {
        pattern: /book|therapist|find.*(help|support)|who.*see/i,
        reply: {
            answer: 'To book a therapist: open Home for recommendations, or go to the Therapy tab and tap Book. You can browse all verified therapists, view their profile, then pick a time and pay in naira. A free assessment on Home helps us suggest a better match.',
            navHints: [
                { label: 'Browse therapists', screen: 'TherapistList' },
                { label: 'Take assessment', screen: 'Assessment' },
            ],
        },
    },
    {
        pattern: /session|appointment|upcoming|join|video|call/i,
        reply: {
            answer: 'Your sessions live on the Therapy tab. Upcoming bookings show date, time, and a Join button when it is time. You can reschedule from the session details. Past sessions appear below for your records.',
            navHints: [{ label: 'Open Therapy', screen: 'TherapyTab' }],
        },
    },
    {
        pattern: /mood|feeling|check.?in|streak/i,
        reply: {
            answer: 'Log how you feel on the Home tab — tap an emoji under “How are you feeling today?”. Your streak and mood count appear on Home after you check in. Tools has deeper exercises if you need more support.',
            navHints: [{ label: 'Go to Home', screen: 'HomeTab' }],
        },
    },
    {
        pattern: /assess|phq|gad|quiz|screening/i,
        reply: {
            answer: 'The free PHQ-9 and GAD-7 assessment takes about two minutes. It helps match you with suitable therapists and tracks progress over time. Start from Home if you have not done it, or open Progress in Tools to see scores over time.',
            navHints: [
                { label: 'Start assessment', screen: 'Assessment' },
                { label: 'View progress', screen: 'Progress' },
            ],
        },
    },
    {
        pattern: /journal|write|worry|dump|reflect/i,
        reply: {
            answer: 'Journal is private to you. Use Worry dump in Tools for a quick brain dump, or Journal for longer entries. Writing things down can ease racing thoughts before sleep or therapy.',
            navHints: [
                { label: 'Worry dump', screen: 'WorryDump' },
                { label: 'Open journal', screen: 'Journal' },
            ],
        },
    },
    {
        pattern: /breath|calm|anxious|panic|overwhelm|stress/i,
        reply: {
            answer: 'If you feel overwhelmed: try Quick calm (box breathing) or the 5-4-3-2-1 grounding exercise in Tools. Breathe slowly — in for 4, out for 6. If you are in crisis, use Crisis Support for hotlines or call 112.',
            navHints: [
                { label: 'Quick calm', screen: 'ActivityTimer', params: { slug: 'box-breathing' } },
                { label: 'Grounding', screen: 'Grounding' },
                { label: 'Crisis support', screen: 'CrisisSupport' },
            ],
        },
    },
    {
        pattern: /refer|wallet|credit|invite|earn/i,
        reply: {
            answer: 'Refer friends from the banner on Home or Refer & Earn in your wallet icon (top right on Home/Therapy). When someone signs up with your code, you both get wallet credit toward sessions.',
            navHints: [{ label: 'Refer & earn', screen: 'Referral' }],
        },
    },
    {
        pattern: /article|learn|read|psycho/i,
        reply: {
            answer: 'Learn & Grow articles are on Home (featured previews) and the full library under Learn from the stack. Topics include anxiety, habits, and when to seek therapy.',
            navHints: [
                { label: 'Browse articles', screen: 'Learn' },
                { label: 'Go to Home', screen: 'HomeTab' },
            ],
        },
    },
    {
        pattern: /group|workshop/i,
        reply: {
            answer: 'Group sessions and workshops are listed under Group Sessions in Tools. You can join psychoeducation groups led by licensed facilitators.',
            navHints: [{ label: 'Group sessions', screen: 'GroupSessions' }],
        },
    },
    {
        pattern: /badge|streak|reward|milestone/i,
        reply: {
            answer: 'Badges reward consistent self-care — mood check-ins, journal entries, activities, and sessions. See your streak on Home; open My badges in Tools for full details.',
            navHints: [
                { label: 'My badges', screen: 'Badges' },
                { label: 'Home streak', screen: 'HomeTab' },
            ],
        },
    },
    {
        pattern: /profile|setting|language|password|emergency|contact|notif/i,
        reply: {
            answer: 'Profile tab holds your name, phone, emergency contact, preferred language, notification settings, and sign-out. Add an emergency contact so support people can be reached if needed.',
            navHints: [{ label: 'Open Profile', screen: 'ProfileTab' }],
        },
    },
    {
        pattern: /crisis|suicide|hurt myself|emergency|hotline|help now/i,
        reply: {
            answer: 'If you are in immediate danger, call 112 or go to the nearest emergency room. For crisis counseling, open Crisis Support in Tools for Nigerian hotlines — free and confidential. You are not alone.',
            navHints: [{ label: 'Crisis hotlines', screen: 'CrisisSupport' }],
        },
    },
    {
        pattern: /activity|meditat|mindful|sleep|wind/i,
        reply: {
            answer: 'Wellness Activities in Tools include guided meditation, box breathing, body scan, and gratitude prompts. Quick calm starts box breathing immediately; Sleep wind-down opens a calming meditation.',
            navHints: [
                { label: 'All activities', screen: 'Activities' },
                { label: 'Quick calm', screen: 'ActivityTimer', params: { slug: 'box-breathing' } },
                {
                    label: 'Sleep wind-down',
                    screen: 'ActivityTimer',
                    params: { slug: 'guided-meditation' },
                },
            ],
        },
    },
    {
        pattern: /prep|before (my )?session|what to (talk|discuss)/i,
        reply: {
            answer: 'Session prep in Tools helps you note what is on your mind before therapy. You can save notes to your journal so you do not forget important topics in the session.',
            navHints: [{ label: 'Session prep', screen: 'SessionPrep' }],
        },
    },
    {
        pattern: /pay|payment|naira|paystack|refund/i,
        reply: {
            answer: 'Sessions are paid securely in naira via Paystack when you book. Wallet credit from referrals applies at checkout. For payment issues, check your booking on the Therapy tab or contact support through Profile.',
            navHints: [{ label: 'Therapy bookings', screen: 'TherapyTab' }],
        },
    },
];
const DEFAULT_REPLY = {
    answer: 'I can help you find features in MyLighthouse — booking therapists, sessions, mood tracking, assessments, journal, activities, referrals, and crisis support. Try asking “How do I book a therapist?” or tap a suggestion below. For clinical emergencies, use Crisis Support or call 112.',
    navHints: [
        { label: 'Crisis support', screen: 'CrisisSupport' },
        { label: 'Browse tools', screen: 'ToolsTab' },
    ],
};
export function findAppGuideAnswer(message) {
    const trimmed = message.trim();
    if (!trimmed)
        return DEFAULT_REPLY;
    for (const { pattern, reply } of NAV_PATTERNS) {
        if (pattern.test(trimmed))
            return reply;
    }
    return DEFAULT_REPLY;
}
//# sourceMappingURL=appGuide.js.map